import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { berserkr, getSupabase, isSupabaseConfigured } from "../lib/supabase";

export interface InferenceCitation {
  chunk_id: string;
  evidence_id: string;
  source_label: string;
  occurred_on: string | null;
  snippet: string;
}
export interface InferenceMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  citations: InferenceCitation[];
  considered: { text?: string } | null;
  created_at: string;
}
export interface InterrogateResult {
  answer: string;
  citations: InferenceCitation[];
  evidence_gap: boolean;
  needed_evidence: string;
  suggests_rerun: boolean;
  rerun_reason: string;
}

/** Start (or resume) the chat thread for one inference, and read its messages. */
export function useInferenceThread(args: {
  caseId?: string;
  surface: string;
  refId?: string | null;
  label?: string;
  enabled?: boolean;
}) {
  const { caseId, surface, refId = null, label, enabled = true } = args;
  const [threadId, setThreadId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !caseId || threadId || !isSupabaseConfigured()) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await berserkr().rpc("start_or_get_inference_thread", {
        p_case_id: caseId, p_surface: surface, p_ref_id: refId, p_title: label ?? null,
      });
      if (!cancelled && !error && data) setThreadId((data as { id: string }).id);
    })();
    return () => { cancelled = true; };
  }, [enabled, caseId, surface, refId, label, threadId]);

  const messages = useQuery<InferenceMessage[], Error>({
    queryKey: ["inference-thread", threadId],
    enabled: isSupabaseConfigured() && !!threadId,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("fetch_inference_thread", { p_thread_id: threadId });
      if (error) throw new Error(error.message);
      return (data as { messages?: InferenceMessage[] } | null)?.messages ?? [];
    },
  });

  return { threadId, messages };
}

/** Ask the inference a question (and optionally "consider X" / supply evidence). */
export function useInterrogate(caseId: string | undefined, threadId: string | null) {
  const qc = useQueryClient();
  return useMutation<
    InterrogateResult,
    Error,
    { surface: string; refId?: string | null; label?: string; context?: string; question: string; considered?: string }
  >({
    mutationFn: async (vars) => {
      const sb = getSupabase();
      if (!sb) throw new Error("Supabase not configured");
      if (!threadId) throw new Error("thread not ready yet");
      const { data, error } = await sb.functions.invoke("interrogate-inference", {
        body: {
          case_id: caseId, thread_id: threadId, surface: vars.surface, ref_id: vars.refId ?? null,
          inference_label: vars.label, inference_context: vars.context,
          question: vars.question, considered: vars.considered,
        },
      });
      if (error) throw new Error(error.message);
      const d = data as InterrogateResult & { error?: string };
      if (d?.error) throw new Error(d.error);
      return d;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inference-thread", threadId] }),
  });
}

/** Capture a thumbs / flag + note on an inference, for the retuning queue. */
export function useRecordInferenceFeedback(caseId: string | undefined) {
  return useMutation<
    string,
    Error,
    { surface: string; refId?: string | null; rating: "up" | "down" | "flag"; note?: string; threadId?: string | null; label?: string }
  >({
    mutationFn: async (vars) => {
      const { data, error } = await berserkr().rpc("record_inference_feedback", {
        p_case_id: caseId, p_surface: vars.surface, p_ref_id: vars.refId ?? null,
        p_rating: vars.rating, p_note: vars.note ?? null,
        p_thread_id: vars.threadId ?? null, p_label: vars.label ?? null,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
  });
}
